import { Injectable, inject, signal, computed } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, ReplaySubject, fromEvent } from 'rxjs';
import { debounceTime, distinctUntilChanged, pairwise, startWith, takeUntil } from 'rxjs/operators';

import { LaunchDarklyFlags } from 'app/core/services/launch-darkly/launch-darkly.types';
import { coreSelectors, getFeatureFlagObservable } from 'app/core/state';
import { Loggable } from 'app/utils/logging/loggable';
import Social from 'entity/Social';
import User from 'entity/User';
import QuestionInputType from 'enums/QuestionInputType';
import {
  BadgeDataValue,
  BaseUser,
  GetUsersMessage,
  IncomingMessage,
  IncomingMessageType,
  OutgoingMessageType,
} from 'messages/websocket.messages';

import { GalleryTabs, GalleryTabsTypes } from '../../presentation/presentation-drawer/gallery/constants';
import { ParticipantMediaState, actions, selectors, socialAppActions, socialAppSelectors } from '../../state/app';
import { strToHex } from '../../utils/theme';
import { getPageCount } from '../audience/audience.helper';
import { BadgingService } from '../badging/badging.service';
import { FavoriteService } from '../favorite.service';
import { SceneService } from '../scene.service';
import { GroupShareScreenParticipantService } from '../share-screen-group/group-share-screen-participant.service';
import { SideSectionService } from '../side-section.service';
import { WebsocketService } from '../websocket.service';
import { Timeout } from 'app/types';

export enum Breakpoints {
  XL_DESKTOP = 1920,
  LARGE_DESKTOP = 1440,
  MEDIUM_DESKTOP = 1245,
  DESKTOP = 1024,
  MEDIUM_TABLET = 900,
  TABLET = 700,
}

export const VIDEO_SIZE = 90;
export const LARGE_VIDEO_SIZE = 100;
export const XL_VIDEO_SIZE = 110;
export const MAX_AUDIENCE_SIZE = 20;
export const MIN_AUDIENCE_SIZE = 10;
export const MIN_GALLERY_HEIGHT = 440;
const GALLERY_TIMEOUT = 200;

export const MAX_AUDIENCE_SIZE_V2 = 24;
export const MIN_AUDIENCE_SIZE_V2 = 11;

export const VIDEO_SIZE_V2 = 96;
export const LARGE_VIDEO_SIZE_V2 = 128;
export const SIDE_ARROW_MAX = 96;
export const SIDE_ARROW_MIN = 64;

export const DEFAULT_GALLERY_TAB = {
  icon: 'dashboard',
  name: 'Gallery',
  isSelected: true,
  type: GalleryTabsTypes.ALL,
  isDeleteable: false,
};

export interface CustomGalleryTabs {
  type: string;
  title: string;
  question: string;
}

export const MAX_BADGE_ANSWER_SIZE = 20;

export interface groupParticipants {
  groupName: string;
  participants: User[];
}

export const DEFAULT_GROUP_NAMES = {
  GROUP: 'In my group',
  ROOM: 'In my room',
  SCOOTAVERSE: 'In my Scootaverse',
  FAV: 'Favorites',
};

const NOT_ALLOWED_QUESTIONS = ['About Me', 'LinkedIn', 'Email', 'Name', 'First Name', 'Last Name'];
const NOT_ALLOWED_QUESTIONS_TYPE = [QuestionInputType.PARAGRAPH];

type PlayerMediaState = Record<string, ParticipantMediaState>;

@Injectable({
  providedIn: 'root',
})
export class GalleryService extends Loggable {
  private store = inject(Store);
  private sceneService = inject(SceneService);
  private websocketService = inject(WebsocketService);
  private sideSectionService = inject(SideSectionService);
  private groupShareParticipantService = inject(GroupShareScreenParticipantService);
  private badgingService = inject(BadgingService);
  private favoriteService = inject(FavoriteService);

  private readonly user = signal<User | undefined>(undefined);
  private readonly userId = signal<string | undefined>(undefined);
  private readonly socialId = signal<string | undefined>(undefined);
  private readonly participants = signal<User[] | BaseUser[]>([]);

  // Handles Sizing of Gallery Participants
  public readonly gallerySize = signal(MAX_AUDIENCE_SIZE);
  public readonly videoSize = signal(VIDEO_SIZE);

  public readonly pageCountSignal = signal(1);
  public readonly currentPageSignal = signal(1);
  public readonly participantMargin = signal(30);
  public readonly singleRowLayout = signal(false);
  public readonly minContainerMarginTop = signal(32);
  private readonly isFiltering = signal(false);

  // Presentation
  private readonly isPresentationActive = signal(false);
  private readonly presentersIds = signal<string[]>([]);

  // Cross Room Presentation
  private readonly isCrossRoom = signal(false);
  private afterUserId = '';
  private crossParticipants: Map<string, BaseUser> = new Map<string, BaseUser>();

  // Group Share
  private groupIds: string[] = [];
  public isUserSharingToGroup = false;
  public readonly groupSharingUserId = computed(() => {
    return this.groupShareParticipantService.groupShareId();
  });

  // Gallery
  private gallery: User[] = [];
  private destroyed$ = new ReplaySubject<boolean>(1);

  // Gallery tabs
  private roomSetId?: string;
  private badgeParticipantsAnswer: Map<string, BadgeDataValue[]> = new Map<string, BadgeDataValue[]>();
  public customGalleryTabs: CustomGalleryTabs[] = [];
  public readonly hasBadgingQuestions = signal(false);

  public allBaseUsers$: BehaviorSubject<BaseUser[]> = new BehaviorSubject<BaseUser[]>([]);

  public readonly allBaseUsersSignal = signal<BaseUser[]>([]);
  public readonly galleryUsersCount = computed(() => this.allBaseUsersSignal().length);

  // Maximum page number based on gallery users and size
  public readonly maxPage = computed(() =>
    Math.max(1, Math.ceil(this.filteredGalleryUsers().length / this.gallerySize())),
  );

  // Page count computed from filtered gallery users and gallery size
  public readonly pageCount = computed(() => getPageCount(this.filteredGalleryUsers().length, this.gallerySize()));

  public readonly filteredGalleryUsers = computed(() => {
    const users = this.allBaseUsersSignal();
    const presenterIds = this.presentersIds();
    const userId = this.userId();

    return users.filter((user) => !presenterIds.includes(user.userId ?? '') && user.userId !== userId);
  });

  public readonly currentGalleryPage = computed(() => {
    const pageIndex = this.currentPageSignal() - 1;
    const allUsers = this.filteredGalleryUsers();

    const start = pageIndex * this.gallerySize();
    const end = start + this.gallerySize();
    return allUsers.slice(start, end);
  });

  // Computed property to represent the current visible participants
  public readonly currentParticipantsSlice = computed(() => {
    const startIndex = (this.currentPageSignal() - 1) * this.gallerySize();
    return this.participants().slice(startIndex, startIndex + this.gallerySize());
  });

  private readonly presentationV3 = signal(false);

  private galleryTimeoutId?: Timeout;

  private badgeDataRequestTimeout?: Timeout;

  public constructor() {
    super();
    this.setDisplayName('GalleryService');
    // use init method to attach observables
  }

  public init(): void {
    // Initialize destroyed$ as a ReplaySubject to allow for proper cleanup
    // it is completed on presentation close
    this.destroyed$ = new ReplaySubject<boolean>(1);
    this.info(`Initializing gallery service`);

    getFeatureFlagObservable(this.store, LaunchDarklyFlags.presentation_v3)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((presentation_v3) => {
        this.presentationV3.set(presentation_v3);
      });

    this.sideSectionService.openStateChanged$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.setGallerySize(GALLERY_TIMEOUT));

    // Watch for media query changes, audience size depends on the screen size
    fromEvent(window, 'resize')
      .pipe(debounceTime(300), takeUntil(this.destroyed$))
      .subscribe(() => this.setGallerySize());

    this.store
      .select(selectors.getCurrentSocial)
      .pipe(startWith({} as Social), pairwise(), takeUntil(this.destroyed$))
      .subscribe(([prevRoom, room]) => {
        this.socialId.set(room?.socialId);
        if (prevRoom?.socialId !== room?.socialId) {
          this.reset();
        }
      });

    this.store
      .select(coreSelectors.getUser)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((user) => {
        this.user.set(user.user);
        this.userId.set(this.user()?.userId);
      });

    // ** Presentations ** //
    this.store
      .select(selectors.isPresentationActive)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isPresentationActive) => {
        this.isPresentationActive.set(isPresentationActive);
        if (!this.isPresentationActive()) {
          this.reset();
        }
      });

    // Filter participants every time a new presenter jumps into presentation or becomes an audience member.
    this.store
      .select(selectors.getPresenterIds)
      .pipe(distinctUntilChanged(), takeUntil(this.destroyed$))
      .subscribe((presenterIds) => {
        this.presentersIds.set(presenterIds);

        if (this.isCrossRoom()) {
          this.filterCrossRoomPresentation();
          return;
        }
        // Filter single room presentation
        this.filterPresentation();
      });

    // Filter participants if audience count changes
    this.sceneService.sceneManager?.participantsCountChange.pipe(takeUntil(this.destroyed$)).subscribe(() => {
      // Filter cross room if count changes
      if (this.isCrossRoom()) {
        this.filterCrossRoomPresentation();
        return;
      }
      this.setGallerySize();
      this.filterPresentation();
    });

    // Check for change in participant media state and update audience
    this.store
      .select(selectors.getParticipantMediaState)
      .pipe(pairwise(), takeUntil(this.destroyed$))
      .subscribe(([previous, current]) => {
        this.hasRemoteVideoChanged(previous, current);
      });

    // ** Cross Room Presentations ** //
    this.store
      .select(selectors.isCrossRoomPresentation)
      .pipe(startWith(false), pairwise(), takeUntil(this.destroyed$))
      .subscribe(([previousIsCrossRoom, isCrossRoom]) => {
        this.isCrossRoom.set(isCrossRoom);
        if (previousIsCrossRoom && !isCrossRoom) {
          this.currentPageSignal.set(1);
          this.pageCountSignal.set(1);
          this.filterPresentation();
        }

        if (!previousIsCrossRoom && isCrossRoom) {
          this.getCrossRoomUsers();
        }

        if (previousIsCrossRoom && isCrossRoom) {
          this.reset();
          this.getCrossRoomUsers();
        }
      });

    this.websocketService.message$.pipe(takeUntil(this.destroyed$)).subscribe(this.handleWebsocketMessage);

    this.store
      .select(socialAppSelectors.getCurrentConnectedRoomSetId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((roomId) => {
        this.roomSetId = roomId;
      });

    // ** Group Share ** //
    this.store
      .select(selectors.isUserSharingScreenToGroup)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((isUserSharing) => {
        this.isUserSharingToGroup = isUserSharing;
      });

    this.store
      .select(selectors.getMyGroupScreenShare)
      .pipe(takeUntil(this.destroyed$))
      .subscribe((screenShare) => {
        this.groupIds = screenShare?.viewerUserIds ?? [];
        this.filterGroup();
      });

    // ** Gallery **//
    this.store
      .select(selectors.getGallery)
      .pipe(startWith([]), pairwise(), takeUntil(this.destroyed$))
      .subscribe(([, curr]) => {
        this.getGalleryTabs().catch((err) => this.error(err));
        this.gallery = curr;
      });

    this.checkBadgeHasQuestions().catch((error) => this.error(error));
  }

  public destroy(): void {
    this.reset();
    this.destroyed$.next(true);
    this.destroyed$.complete();

    // Clear all timeouts
    clearTimeout(this.galleryTimeoutId);
    clearTimeout(this.badgeDataRequestTimeout);
  }

  /**
   * Sets gallery size based on the current size of the drawer
   * Sets Video Bubble size and participant size
   * Timeout = 0 unless we setGallerySize when side menu is opened and closed
   * Allows for gallery to resize prior to retrieving gallery width and height
   */

  public setGallerySize(timeout = 0) {
    this.galleryTimeoutId = setTimeout(() => {
      const id = 'gallery-tabs';
      const containerWidth = document.getElementById(id)?.clientWidth;
      const containerHeight = document.getElementById(id)?.clientHeight;
      if (containerWidth && containerHeight) {
        // Presentation V3 - responsive gallery
        if (this.presentationV3()) {
          this.videoSize.set(98);
          this.singleRowLayout.set(true);
          const margin = 30;
          const size = Math.floor(containerWidth / (this.videoSize() + margin));
          const minSize = 12;
          this.gallerySize.set(size > minSize ? minSize : size);
          this.setCurrentGallery(this.currentPageSignal());
          return;
        }

        // Presentation V2 - responsive gallery
        // Check current height of gallery
        const minContainerHeight = MIN_GALLERY_HEIGHT;
        if (containerHeight >= minContainerHeight) {
          this.singleRowLayout.set(false);
        } else {
          this.singleRowLayout.set(true);
        }

        // Check for large screens
        if (containerWidth >= Breakpoints.XL_DESKTOP) {
          this.videoSize.set(XL_VIDEO_SIZE);
          this.participantMargin.set(50);
        } else if (containerWidth >= Breakpoints.LARGE_DESKTOP) {
          this.videoSize.set(LARGE_VIDEO_SIZE);
          this.participantMargin.set(40);
        } else {
          this.videoSize.set(VIDEO_SIZE);
          this.participantMargin.set(30);
        }

        if (this.participants().length) {
          this.participantMargin.set(16);
          if (containerWidth > Breakpoints.LARGE_DESKTOP && this.participants().length <= 10) {
            this.videoSize.set(LARGE_VIDEO_SIZE_V2);
            this.minContainerMarginTop.set(32);
          } else if (containerWidth > Breakpoints.MEDIUM_DESKTOP && this.participants().length <= 8) {
            this.videoSize.set(LARGE_VIDEO_SIZE_V2);
            this.minContainerMarginTop.set(32);
          } else if (containerWidth > Breakpoints.DESKTOP && this.participants().length <= 7) {
            this.videoSize.set(LARGE_VIDEO_SIZE_V2);
            this.minContainerMarginTop.set(32);
          } else if (containerWidth < Breakpoints.DESKTOP) {
            this.minContainerMarginTop.set(16);
            this.videoSize.set(VIDEO_SIZE_V2);
            if (this.singleRowLayout()) {
              this.participantMargin.set(this.isFiltering() ? 24 : 12);
            }
          } else {
            this.videoSize.set(VIDEO_SIZE_V2);
          }
        }

        // Set side arrows container size
        const sideArrowSize =
          containerWidth > Breakpoints.MEDIUM_TABLET ? (containerWidth === 1400 ? 0 : SIDE_ARROW_MAX) : SIDE_ARROW_MIN;

        // Set number of participants displayed in gallery
        const margin = this.participantMargin();
        const size = Math.floor((containerWidth - sideArrowSize) / (this.videoSize() + margin));
        const maxSize = MAX_AUDIENCE_SIZE_V2;
        const minSize = MIN_AUDIENCE_SIZE_V2;
        const maxRowSize = maxSize / 2;

        if (size >= maxRowSize && !this.singleRowLayout()) {
          const gallerySize = maxSize;
          this.gallerySize.set(gallerySize);
        } else if (size < minSize && !this.singleRowLayout()) {
          this.gallerySize.set(this.roundToEvenNumber(size) * 2);
        } else if (this.singleRowLayout()) {
          this.gallerySize.set(size > minSize ? minSize : size);
        }

        // Set page count and gallery
        this.setCurrentGallery(this.currentPageSignal());
      }
    }, timeout);
  }

  private setCurrentGallery(page: number) {
    const newPageCount = getPageCount(this.participants().length, this.gallerySize());
    const currentPage = page === 0 ? 1 : page;
    //if page number passed is greater than page count we get a ghost page bug.
    //so we use a local variable to make sure the page used is equal or lower than page count.
    const _page = newPageCount >= currentPage ? currentPage : newPageCount;
    const startIndex = (_page - 1) * this.gallerySize();
    const gallery = this.participants().slice(startIndex, startIndex + this.gallerySize());
    const newGalleryIds = gallery.map((user) => user.userId ?? '');
    const currGalleryIds = this.gallery.map((user) => user.userId ?? '');
    const galleryIsTheSame = this.galleryIsTheSame(newGalleryIds, currGalleryIds);
    if (!galleryIsTheSame) {
      this.store.dispatch(actions.SET_GALLERY({ gallery }));
    }
    this.pageCountSignal.set(newPageCount);
    this.currentPageSignal.set(_page);
  }

  private galleryIsTheSame(a: string[] = [], b: string[]) {
    return a.toString() === b.toString();
  }

  public previousPage() {
    if (this.currentPageSignal() > 1) {
      this.setCurrentGallery(this.currentPageSignal() - 1);
    }
  }

  public nextPage() {
    if (this.currentPageSignal() < this.pageCountSignal()) {
      this.setCurrentGallery(this.currentPageSignal() + 1);
    }
  }

  public previousPageSignal(): void {
    this.setPageSignal(this.currentPageSignal() - 1);
  }

  public nextPageSignal(): void {
    this.setPageSignal(this.currentPageSignal() + 1);
  }

  private roundToEvenNumber(number: number): number {
    if (number % 2 === 0) {
      return number; // Number is already even
    } else {
      return (number - 1) % 2 === 0 ? number - 1 : number - 2;
    }
  }

  /**
   * Resets audience
   */
  public reset(): void {
    this.store.dispatch(actions.SET_GALLERY({ gallery: [] }));
    this.currentPageSignal.set(1);
    this.pageCountSignal.set(1);
    this.participants.set([]);
    this.groupIds = [];
    this.afterUserId = '';
    this.crossParticipants.clear();
  }

  // Only handles audience members for presentations
  private handleWebsocketMessage = (msg: IncomingMessage): void => {
    const { t: type } = msg;

    const updateGalleryTabs = [
      IncomingMessageType.LEFT,
      IncomingMessageType.USER_LEFT,
      IncomingMessageType.CON,
      IncomingMessageType.PARTICIPANT_COUNT_UPDATE,
    ];

    if (updateGalleryTabs.includes(type)) {
      // update the gallery tabs when users join or left the gallery
      this.getGalleryTabs().catch((err) => this.error(err));
    }

    if (type === IncomingMessageType.BADGE_DATA) {
      // add Badge awnsers to gallery tabs
      if (this.badgeParticipantsAnswer.has(msg.userId)) {
        this.badgeParticipantsAnswer.set(msg.userId, msg.data);
        this.store.dispatch(socialAppActions.USER_EDIT_BADGE({ userId: msg.userId }));
      } else {
        this.badgeParticipantsAnswer.set(msg.userId, msg.data);
      }
      return;
    }

    if (type === IncomingMessageType.LEFT) {
      // TODO: User cleanup?
    }

    // Cross Room Presentations only
    if (!this.isCrossRoom()) {
      return;
    }
    switch (type) {
      case IncomingMessageType.USERS: {
        if (!msg.users.length) {
          return;
        }

        // Track if we actually need to update the UI
        let hasChanges = false;

        // Filter results
        for (const user of msg.users) {
          const { publishing, socialId, userId } = user;
          if (!this.crossParticipants.has(userId) && socialId !== this.socialId() && publishing) {
            this.crossParticipants.set(userId, user);
            hasChanges = true;
          }
        }

        const participants = this.sceneService.sceneManager?.participantsAndPlayerList ?? [];
        for (const p of participants) {
          if (!this.crossParticipants.has(p.userId)) {
            const { firstName, profileImageBackground, profileImageURI, userId } = p.user;
            if (userId) {
              this.crossParticipants.set(userId, {
                userId,
                firstName,
                profileImageBackground,
                profileImageURI,
                socialId: this.participants().find((pi) => pi.userId === pi.userId)?.socialId,
                micOn: p.remoteMicEnabled,
                cameraOn: p.remoteVideoEnabled,
              });
              hasChanges = true;
            }
          }
        }

        // Set last id received
        const afterUser = Object.keys(this.crossParticipants).pop();
        if (afterUser) {
          this.afterUserId = afterUser;
        }

        if (hasChanges) {
          this.filterCrossRoomPresentation();
        } else {
          this.debug('No participant changes, skipping cross-room update');
        }
        return;
      }

      case IncomingMessageType.USER_LEFT: {
        // Remove participant if they left cross room
        const hasUser = this.crossParticipants.has(msg.userId);
        if (hasUser) {
          this.crossParticipants.delete(msg.userId);
          this.filterCrossRoomPresentation();
        }
        return;
      }

      case IncomingMessageType.USER: {
        // Add user if they joined cross room
        const { user, userId } = msg;

        if (!this.crossParticipants.has(userId) && user.publishing) {
          this.crossParticipants.set(userId, user);
          this.filterCrossRoomPresentation();
        } else if (this.crossParticipants.has(userId)) {
          // Check if any important properties changed
          const existing = this.crossParticipants.get(userId);
          const hasChanged =
            existing?.micOn !== user.micOn ||
            existing?.cameraOn !== user.cameraOn ||
            existing?.publishing !== user.publishing;

          if (hasChanged) {
            this.crossParticipants.set(userId, user);
            this.filterCrossRoomPresentation();
          }
        }
        return;
      }

      case IncomingMessageType.CON: {
        if (!this.crossParticipants.has(msg.userId)) {
          this.crossParticipants.set(msg.userId, msg.user);
          this.filterCrossRoomPresentation();
        }
        return;
      }

      case IncomingMessageType.LEFT: {
        if (this.crossParticipants.has(msg.userId)) {
          this.crossParticipants.delete(msg.userId);
          this.filterCrossRoomPresentation();
        }
        return;
      }
    }
  };

  /**
   * CROSS ROOM: Filters presenters from the participants array and sort participants
   */

  private getCrossRoomUsers(): void {
    const msg: GetUsersMessage = {
      t: OutgoingMessageType.GET_USERS,
      maxCount: 100,
      afterUserId: this.afterUserId,
    };
    this.websocketService.sendMessage(msg);
  }

  // Cache the last ids to prevent redundant updates
  private _lastParticipantIds: string[] = [];

  /**
   * Filters presenters from the participants array and sorts participants
   * Uses caching to prevent unnecessary updates
   */
  private filterCrossRoomPresentation(): void {
    if (!this.isPresentationActive() || !this.isCrossRoom()) {
      return;
    }

    // Pull users from map
    const baseUsers: BaseUser[] = [];
    for (const [, user] of this.crossParticipants.entries()) {
      baseUsers.push(user);
    }

    // Check if participants have actually changed
    const currentIds = baseUsers.map((user) => user.userId).filter((id) => !!id) as string[];
    const hasChanges =
      this._lastParticipantIds.length !== currentIds.length ||
      !this._lastParticipantIds.every((id) => currentIds.includes(id));

    if (!hasChanges) {
      this.debug('No participant changes, skipping gallery update');
      return;
    }

    // Update cache
    this._lastParticipantIds = currentIds;

    this.allBaseUsers$.next(baseUsers);
    this.allBaseUsersSignal.set(baseUsers); // Update the signal
    // Sort combined gallery by camera status and crossParticipants last
    baseUsers.sort((a, b) => {
      if (a.cameraOn === b.cameraOn && a.socialId === b.socialId) {
        return 0;
      } else if (a.cameraOn && a.socialId === this.socialId()) {
        return -1;
      } else {
        return 1;
      }
    });

    // Filter combined gallery
    this.participants.set(
      baseUsers
        .map((p) => p)
        .filter((p) => !this.presentersIds().includes(p.userId ?? '') && p.userId !== this.userId()),
    );
    // Set main player as first person in gallery
    if (!this.presentersIds().includes(this.userId() ?? '')) {
      const user = this.user();
      if (user && user.userId) {
        const { firstName, lastName, userId } = user;
        const baseUser: BaseUser = { firstName, lastName, userId };
        this.participants.update((participantsArray) => [baseUser, ...participantsArray]);
      }
    }
    this.setCurrentGallery(this.currentPageSignal());
  }

  /**
   * Filters presenters from the participants array and sort participants
   */
  private filterPresentation(): void {
    if (!this.isPresentationActive() || this.isCrossRoom()) {
      return;
    }

    const participants = this.sceneService.sceneManager?.participantsAndPlayerList ?? [];
    participants.sort((a, b) => (a.remoteVideoEnabled === b.remoteVideoEnabled ? 0 : a.remoteVideoEnabled ? -1 : 1));
    this.participants.set(
      participants
        .map((p) => p.user)
        .filter((p) => !this.presentersIds().includes(p.userId ?? '') && p.userId !== this.userId()),
    );
    const currentUser = this.user();
    if (currentUser && !this.presentersIds().includes(this.userId() ?? '')) {
      if (currentUser) {
        this.participants.update((participantsArray) => [currentUser, ...participantsArray]);
      }
    }
    this.setCurrentGallery(this.currentPageSignal());
  }

  /**
   * Filter audience by whether the participants video is enabled
   */
  private hasRemoteVideoChanged(previous: PlayerMediaState, current: PlayerMediaState): void {
    if (this.isCrossRoom()) {
      return;
    }
    Object.keys(current).forEach((key) => {
      if (previous[key]) {
        if (previous[key].videoMuted !== current[key].videoMuted) {
          this.filterPresentation();
        }
      }
    });
  }

  /**
   * Filter group participants into audience
   */
  private filterGroup(): void {
    if (!this.isPresentationActive()) {
      // Sort group by videoEnabled
      const participants = this.sceneService.sceneManager?.participantsAndPlayerList ?? [];
      const group = participants.filter((user) => this.groupIds.includes(user.userId));

      group.sort((a, b) => (a.remoteVideoEnabled === b.remoteVideoEnabled ? 0 : a.remoteVideoEnabled ? -1 : 1));
      this.participants.set(
        group.map((p) => p.user).filter((p) => p.userId !== this.groupSharingUserId() && p.userId !== this.userId()),
      );
      if (this.user() && this.userId() !== this.groupSharingUserId()) {
        const user = this.user();
        if (user) {
          this.participants.update((participantsArray) => [user, ...participantsArray]);
        }
      }
    }
    this.setCurrentGallery(this.currentPageSignal());
  }

  public async getGalleryTabs(): Promise<void> {
    this.customGalleryTabs = [];
    if (!this.roomSetId) {
      return;
    }

    const badgeQuestions = await this.badgingService.getBadgeQuestions(this.roomSetId);

    const galleryQuestions = badgeQuestions
      .filter(
        (question) =>
          !NOT_ALLOWED_QUESTIONS.includes(question.title || '') &&
          !NOT_ALLOWED_QUESTIONS_TYPE.includes(question.inputType || 0),
      )
      .map((question) => ({
        type: GalleryTabsTypes.BADGE_QUESTION,
        title: question?.title || '',
        question: question?.title || '',
      }));

    this.customGalleryTabs = [...galleryQuestions];

    this.participants().forEach((participant) => {
      if (participant.userId && !this.badgeParticipantsAnswer.has(participant.userId)) {
        this.badgingService.getUserBadgeData(participant.userId);
      }
    });

    this.mapGalleryAnswers();
  }

  public getRingColor(userId: string): string | undefined {
    if (userId) {
      const player = this.sceneService.getPlayer(userId);
      return player?.ringColor;
    }
    return;
  }

  public convertRingColor(ringColor?: string): number | undefined {
    if (ringColor && ringColor.length > 3 && ringColor.indexOf('#') === 0) {
      return strToHex(ringColor);
    } else {
      return;
    }
  }

  private mapGalleryGroups(gallery: User[]) {
    const galleryGroup: groupParticipants[] = [];
    galleryGroup.push({
      groupName: DEFAULT_GROUP_NAMES.ROOM,
      participants: gallery,
    });

    return galleryGroup;
  }

  private mapGalleryAnswers() {
    const titles: string[] = [];
    this.badgeParticipantsAnswer.forEach((value) => {
      const badgeAnswers = value
        .filter(
          (answer) =>
            (!NOT_ALLOWED_QUESTIONS.includes(answer.propertyName) &&
              answer?.value?.length &&
              answer.value?.length < MAX_BADGE_ANSWER_SIZE) ||
            answer.multiValues?.length,
        )
        .map((answer) => {
          const answerTitle = answer.value?.toString() ?? answer.multiValues?.toString() ?? '';
          if (titles.includes(answerTitle.toLowerCase())) {
            return;
          }
          titles.push(answerTitle.toLowerCase());
          return {
            type: GalleryTabsTypes.BADGE_ANSWER,
            title: answerTitle,
            question: answer.propertyName,
          };
        })
        .filter((answer) => !!answer);
      this.customGalleryTabs = [...this.customGalleryTabs, ...badgeAnswers];
    });
  }

  private async checkBadgeHasQuestions(): Promise<void> {
    const badgeQuestions = await this.badgingService.getMyBadgeData(this.roomSetId ?? '');
    this.hasBadgingQuestions.set(!!badgeQuestions.length);
  }

  public setPageSignal(page: number): void {
    // Constrain page number using the computed maxPage
    const newPage = Math.max(1, Math.min(page, this.maxPage()));

    // Only update if the page has changed
    if (this.currentPageSignal() !== newPage) {
      this.currentPageSignal.set(newPage);
    }
  }

  /**
   * Filter gallery participants from a gallery tab
   * @param galleryTab selected option to filter
   * @param gallery the gallery that you want to filter
   */
  public filterGallery(galleryTab: GalleryTabs, gallery = this.gallery) {
    if (galleryTab.type === GalleryTabsTypes.ALL) {
      this.isFiltering.set(false);
      const galleryGroup: groupParticipants[] = this.mapGalleryGroups(gallery);
      return galleryGroup;
    }

    if (galleryTab.type === GalleryTabsTypes.FAVORITES) {
      this.isFiltering.set(false);
      const favorites = this.favoriteService.getFavoriteList();
      const favoritesList: groupParticipants[] = [
        {
          groupName: DEFAULT_GROUP_NAMES.FAV,
          participants: this.participants().filter((participant) => favorites.includes(participant.userId || '')),
        },
      ];
      return favoritesList;
    }

    const participants = [...this.badgeParticipantsAnswer]
      .map(([userId, properties]) => {
        return properties.map((property) => ({
          userId,
          propertyName: property.propertyName,
          value: property.value ?? property.multiValues ?? '',
        }));
      })
      .flat();

    if (galleryTab.type === GalleryTabsTypes.BADGE_QUESTION) {
      this.isFiltering.set(true);
      const participantsFilter = participants.filter((participant) => participant.propertyName === galleryTab.question);
      const galleryGroup: Record<string, groupParticipants> = {};

      participantsFilter.forEach((participant) => {
        const group = participant.value?.toString();
        if (!galleryGroup[group]) {
          galleryGroup[group] = {
            groupName: group,
            participants: [],
          };
        }

        const user = gallery.find((galleryParticipant) => galleryParticipant.userId === participant.userId);

        if (!user) {
          this.info('Error while getting user data');
          return;
        }

        galleryGroup[group].participants.push(user);
      });
      return Object.values(galleryGroup);
    }

    if (galleryTab.type === GalleryTabsTypes.BADGE_ANSWER) {
      this.isFiltering.set(true);
      const participantsFilter = participants.filter(
        (participant) =>
          participant.propertyName?.toLowerCase() === galleryTab.question?.toLowerCase() &&
          participant.value?.toString().toLowerCase() === galleryTab.name?.toLowerCase(),
      );

      const galleryGroup: Record<string, groupParticipants> = {};
      participantsFilter.forEach((participant) => {
        const group = participant.value?.toString().toLowerCase();

        if (!galleryGroup[group]) {
          galleryGroup[group] = {
            groupName: group,
            participants: [],
          };
        }

        const user = gallery.find((galleryParticipant) => galleryParticipant.userId === participant.userId);

        if (!user) {
          this.info('Error while getting user data');
          return;
        }

        galleryGroup[group].participants.push(user);
      });
      return Object.values(galleryGroup);
    }
    this.error('Error while grouping the gallery');
    return;
  }
}
